/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {Component, Prop, Vue} from 'vue-property-decorator'
import {AnalysisVO} from '@/valueObjects/AnalysisVO'
import MessageBox from '@/components/shared/MessageBox'
import {YaraSignatureVO} from '@/valueObjects/YaraSignatureVO'
import {SUCCESS_MESSAGE_TYPE} from '@/utils/constants'

@Component({
    components: {
        MessageBox,
    },
})
export default class AnalysisSignatures extends Vue {
    private orderedTags = ['malware','dropper', 'suspicious', 'uncommon', 'clean']
    private maxPatternCount = 3
    private autoExpandLimit = 3

    private groupedSignatures: {[key: string]: any} = {}
    private displayedBlocks: string[] = []
    private message = 'No signature found.'
    private messageType = SUCCESS_MESSAGE_TYPE

    private categoryHeaderContent: {[key: string]: string} =
        {
            memory_dump: 'in memory dumps',
            source_code: 'in source files',
            androguard: 'in androguard report',
            network: 'in hook',
        }

    @Prop()
    private analysis: AnalysisVO
    @Prop()
    private isFinished: boolean

    public get yaraRulesetList() {
        return this.rulesetSubsetToList(this.analysis.rulesetSubset)
    }

    public mounted() {
        this.groupedSignatures = this.groupAndSortSignaturesByTagAndCategory()

        // Auto-expand categories with less than autoExpandLimit number of signatures
        for (const tag of Object.keys(this.groupedSignatures)) {
            for (const category of Object.keys(this.groupedSignatures[tag])) {
                if (this.groupedSignatures[tag][category].length < this.autoExpandLimit) {
                    this.toggleCategory(tag, category)
                }
            }
        }
    }

    private rulesetSubsetToList(rulesetSubset: string): string[] {
        const rulesetList = []

        for (const ruleset of rulesetSubset.split(',')) {
            const trimmedRuleset = ruleset.trim()
            if (trimmedRuleset !== '') {
                rulesetList.push(trimmedRuleset)
            }
        }

        return rulesetList
    }

    private hasSignatures() {
        return this.analysis && this.analysis.signatures.length > 0
    }

    private clearMessage() {
        this.message = ''
    }

    private groupAndSortSignaturesByTagAndCategory(): {[key: string]: any} {
        const groupedSignatures: {[key: string]: any} = {}

        const tagGroupedSignatures = this.groupByTag(this.analysis.signatures)

        const orderedTagGroupedSignatures = this.sortByTag(tagGroupedSignatures)

        for (const tagName of Object.keys(orderedTagGroupedSignatures)) {
            groupedSignatures[tagName] = this.groupByCategory(orderedTagGroupedSignatures[tagName])
        }
        return groupedSignatures
    }

    private groupByTag(signatures: YaraSignatureVO[]): {[key: string]: any} {
        return this.groupByProperty(signatures, 'tags')
    }

    private groupByCategory(signatures: YaraSignatureVO[]): {[key: string]: any} {
        return this.groupByProperty(signatures, 'category')
    }

    private groupByProperty(signatures: YaraSignatureVO[], propertyName: string): {[key: string]: any} {
        let groupedSignatures: {[key: string]: any} = {}

        for (const signature of signatures) {
            const propertyValue = signature[propertyName]

            if (!Array.isArray(propertyValue)) {
                groupedSignatures = this.addSignature(groupedSignatures, propertyValue, signature)
            } else {
                // if signatures are grouped by tags and one signature has several tags
                // this signature is replicated to be displayed under each tag
                for (const value of propertyValue) {
                    groupedSignatures = this.addSignature(groupedSignatures, value, signature)
                }
            }
        }

        return groupedSignatures
    }

    private addSignature(groupedSignatures: { [key: string]: any }, key: string, signature: YaraSignatureVO): {[key: string]: any} {
        const signatures: {[key: string]: any} = groupedSignatures

        signature.arePatternsExpanded = false

        if (key in groupedSignatures) {
            signatures[key].push(signature)
        } else {
            signatures[key] = [signature]
        }

        return signatures
    }

    private sortByTag(tagGroupedSignatures: { [key: string]: any }): { [key: string]: any } {
        const sorted: { [key: string]: any }  = {}

        const tags = Object.keys(tagGroupedSignatures)
        tags.sort(this.sortTags)

        for (const tag of tags) {
            sorted[tag] = tagGroupedSignatures[tag]
        }
        return sorted
    }

    private sortTags(tagName: string, otherTagName: string): number {
        return this.orderedTags.indexOf(tagName) - this.orderedTags.indexOf(otherTagName)
    }

    private getTagCssClass(tagName: string): string {
        if (this.orderedTags.indexOf(tagName) >= 0 ) {
            return tagName
        }
        return ''
    }

    private toggleBlock(blockName: string): void {
        const index = this.displayedBlocks.indexOf(blockName)
        if (index > -1) {
            this.displayedBlocks.splice(index, 1)
        } else {
            this.displayedBlocks.push(blockName)
        }
    }

    private isBlockExpanded(tagName: string): boolean {
        return this.displayedBlocks.indexOf(tagName) > -1
    }

    private toggleCategory(tagName: string, categoryName: string): void {
        this.toggleBlock(tagName + '.' + categoryName)
    }

    private isCategoryExpanded(tagName: string, categoryName: string): boolean {
        return this.isBlockExpanded(tagName + '.' + categoryName)
    }

    private getGroupedSignatureCount(groupedSignatures: { [key: string]: any }): number {
        let count = 0
        var list =[]
        for (const group of Object.keys(groupedSignatures)) {
            for (const regleYara in groupedSignatures[group]){
                list.push(groupedSignatures[group][regleYara].rulesetName)
            }
            let diffruleset = new Set(list)
            count=diffruleset.size
        }
        return count
    }

    private getMaxNbPatterns(signature: YaraSignatureVO): number {
        if (signature.arePatternsExpanded) {
            return signature.strings.length

        } else if (signature.strings.length <= this.maxPatternCount) {
            signature.arePatternsExpanded = true
            return signature.strings.length

        } else {
            return this.maxPatternCount
        }
    }

    private canExpandPatterns(signature: YaraSignatureVO) {
        return signature.strings.length > this.maxPatternCount
    }

    private expandPatterns(signature: YaraSignatureVO) {
        signature.arePatternsExpanded = true
        // DOM is not rebuilt without this update
        this.$forceUpdate()
    }

    private foldPatterns(signature: YaraSignatureVO) {
        signature.arePatternsExpanded = false
        // DOM is not rebuilt without this update
        this.$forceUpdate()
    }

    private min(a: number, b: number): number {
        return a < b ? a : b
    }
}
